/**
 * 設定のメニュー（023 → #153、docs/62 §2・§6、Figma「設定と自社情報（10/9）」①②③⑥）。iOS のまとまり（components/GroupList.tsx。docs/10 §9.15）。
 *   ・頭の行：会社のアイコン 56（自社情報のロゴ、無ければ頭文字）・会社名・「自社（発行者）情報」・›。押すと /settings/company
 *   ・節：会社／見積・請求／項目と言葉／{schedule}（工程管理のオプションの会社だけ）。行の左に絵（RowIcon）。
 *     出す条件は前の ITEMS と同じ（admin・schedule・anyOf＝使う機能）。中身が0行の節は出さない
 *   ・右の値は2つだけ：振込口座＝既定の口座の銀行名（GET /bank-accounts）、ユーザー管理＝人数（GET /users）
 *   ・退会は朱で一番下（注「30日以内なら戻せます」）。「見積・請求書の取消しと改ページ」の行は「見積・請求」へ移したので無い（★1）
 *   ・担当者：頭の行は押せない（下は「担当者」）＋注1行「会社の設定は管理者が変えます」（id memberNote）＋節「見積」に 見積の雛形（と {schedule}の雛形）
 * 同じ中身を、スマホ版は1つの画面（SettingsMenuPage）に、PC版は2列の左の列（components/SettingsLayout.tsx）に出す。
 * PC版で /settings を開いたら、管理者は /settings/company、担当者は /settings/templates に置き換える（★3）
 * 項目を足すときは ADMIN_SECTIONS に1行足し、画面は /settings/… のサブ画面に置く
 * #185（docs/74 §1・§6）：PC 専用の機能（contract の pcOnly.ts＝帳票デザイン・退会…）はスマホではメニューから消す（灰色で出さない）。
 *   誤って作った空の会社（作った本人・トライアル中・データが空・ほかに使える会社がある＝GET /company/delete-check）は、
 *   一番下の「退会」の代わりに朱の「会社削除」（確認の窓 → すぐに消える）。満たさないときは今の「退会」
 */
import { HOME_MSG, COMPANY_DELETE_MSG, ROLE_LABEL, SETTINGS_MSG, SWITCHER_MSG, TEMPLATE_MSG, axesSettingsTitle, settingsRowOf, type BankAccountsPage, type Company, type UseKey, type UsersPage, type Module } from "@genbaryoku/contract";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Navigate, useLocation } from "react-router";
import { api } from "../api.js";
import { useMe } from "../auth.js";
import { BackButton, useBackNav, usePageName } from "../components/BackButton.jsx";
import { CompanyDeleteDialog, useCompanyDeletable } from "../components/CompanyDelete.jsx";
import { CompanyIcon } from "../components/CompanyIcon.jsx";
import { GroupDangerRow, GroupHeadRow, GroupList, GroupNavRow, GroupSection, RowIcon, type RowIconKey } from "../components/GroupList.jsx";
import { uses } from "../core/uses.js";
import { t } from "../labels.js";
import { usePcOnly } from "../lib/pcOnly.js";
import { currentView } from "../lib/view.js";
import { BANK_ACCOUNTS_KEY } from "./SettingsBankAccounts.jsx";
import { USERS_KEY } from "./SettingsUsers.jsx";

type Item = {
  to: string;
  title: string;
  icon: RowIconKey;
  id: string;
  /** 工程管理のオプションが使える会社だけ（#101） */
  schedule?: boolean;
  /** 使う機能のどれかが ON の会社だけ（#142 段3） */
  anyOf?: UseKey[];
  /** そのモジュールの会社だけ（#233：{order}＝sales） */
  module?: Module;
  /** 右の値（docs/62 §2） */
  value?: "bank" | "users";
};
type Section = { key: string; title: string; items: Item[] };

const TEMPLATES: Item = { to: "/settings/templates", title: TEMPLATE_MSG.listTitle, icon: "copy", id: "menuTemplates" }; // #197：「見積の雛形」から
const SCHEDULE_TEMPLATES: Item = { to: "/settings/schedule-templates", title: "{schedule}のテンプレート", icon: "gantt", id: "menuScheduleTemplates", schedule: true };

/** 管理者の節（Figma ①②） */
const ADMIN_SECTIONS: Section[] = [
  {
    key: "company",
    title: SETTINGS_MSG.sectionCompany,
    items: [
      { to: "/settings/bank-accounts", title: "振込口座", icon: "bank", id: "menuBankAccounts", value: "bank" },
      { to: "/settings/users", title: "ユーザー管理", icon: "users", id: "menuUsers", value: "users" },
      // #142 段3：使う機能（7つのスイッチ。#149 で見積依頼。OFF は隠すだけ）
      { to: "/settings/features", title: "使う機能", icon: "toggle", id: "menuFeatures" },
      // #189：自動ログアウト（会社の無操作の上限。全ユーザーに効く）
      { to: "/settings/auto-logout", title: "自動ログアウト", icon: "logout", id: "menuAutoLogout" },
      // #268 1.5c-1：データの取込（未入金の請求書。取込の履歴と取消）
      { to: "/settings/imports", title: "データの取込", icon: "upload", id: "menuImports" },
    ],
  },
  {
    key: "estimate",
    title: SETTINGS_MSG.sectionEstimate,
    items: [
      // ★1：出した見積・請求書の取消し・改ページなど9つもここ（前は自社情報の下）
      { to: "/settings/estimate", title: "見積・請求", icon: "doc", id: "menuEstimate" },
      { to: "/settings/rounding", title: "端数処理", icon: "calc", id: "menuRounding" },
      // #268 1.5b：帳票番号（種類ごとの書式・次の番号・手入力）
      { to: "/settings/numbering", title: "帳票番号", icon: "hash", id: "menuNumbering" },
      TEMPLATES,
      // #119：見積依頼（回答期限の日数・条件・メールの既定文）。使う機能「{vendor}への見積依頼」（rfq。#149）の会社だけ
      { to: "/settings/rfq", title: "見積依頼", icon: "send", id: "menuRfq", anyOf: ["rfq"] },
      // #233：{order}（名称・電子承諾・約款・変更協議・印字項目。docs/93 §4.6）。モジュール sales の会社だけ
      { to: "/settings/orders", title: "{order}", icon: "doc", id: "menuOrders", module: "sales" },
      // #223：送付状（印刷・FAX の1枚目。テンプレート・印刷で送付状・窓付封筒の枠）。両製品・全部の会社
      { to: "/settings/cover-letters", title: "送付状", icon: "mail", id: "menuCoverLetters" },
      { to: "/settings/paper", title: "帳票デザイン（紙の書式）", icon: "layout", id: "menuPaper" },
    ],
  },
  {
    key: "words",
    title: SETTINGS_MSG.sectionWords,
    items: [
      // #118：工種・場所の雛形・品目を1つの画面のタブに（/settings/trades・/places・/items）。名前は言葉と使う機能で（axesSettingsTitle）
      { to: "/settings/trades", title: "{trade}・場所・品目", icon: "tag", id: "menuTrades", anyOf: ["trades", "places", "items"] },
      { to: "/settings/words", title: "言葉（項目名）の言い換え", icon: "text", id: "menuWords" },
      // #164：単位（明細行・品目の単位の欄の候補。両製品）
      { to: "/settings/units", title: "単位", icon: "ruler", id: "menuUnits" },
    ],
  },
  {
    key: "schedule",
    title: SETTINGS_MSG.sectionSchedule,
    items: [{ to: "/settings/schedule-calendar", title: "{schedule}の休日", icon: "calendar", id: "menuScheduleCalendar", schedule: true }, SCHEDULE_TEMPLATES],
  },
];
/** 担当者の節（Figma ③） */
const MEMBER_SECTIONS: Section[] = [{ key: "member", title: SETTINGS_MSG.sectionMemberEstimate, items: [TEMPLATES, SCHEDULE_TEMPLATES] }];

/** 設定のメニューの中身（頭の行・節・退会）。column＝PC の2列の左の列（いまの画面の行に印） */
export function SettingsMenu({ column = false }: { column?: boolean }) {
  const me = useMe();
  const loc = useLocation();
  const admin = me.data?.user.role === "admin";
  const schedule = !!me.data?.features.schedule;
  const company = useQuery({ queryKey: ["company"], queryFn: () => api<Company>("GET", "/company") });
  const bank = useQuery({ queryKey: BANK_ACCOUNTS_KEY, queryFn: () => api<BankAccountsPage>("GET", "/bank-accounts"), enabled: admin });
  const users = useQuery({ queryKey: USERS_KEY, queryFn: () => api<UsersPage>("GET", "/users"), enabled: admin });
  // PC 専用の機能（#185）：スマホでは出さない。退会の節（＝この会社を削除）も PC だけ
  const pcOnly = usePcOnly();
  const closeShown = admin && !pcOnly.hidden("/settings/close");
  const deletable = useCompanyDeletable(closeShown)?.deletable === true;
  const [askDelete, setAskDelete] = useState(false);
  if (!me.data) return null;
  const m = me.data;
  const cur = column ? settingsRowOf(loc.pathname) : null;
  const name = company.data?.name || m.tenant.name;
  const valueOf = (v: Item["value"]): string | undefined => {
    if (v === "bank") return bank.data?.accounts.find((a) => a.isDefault)?.bankName;
    if (v === "users") return users.data ? SETTINGS_MSG.usersCount.replace("{n}", String(users.data.users.length)) : undefined;
    return undefined;
  };
  const titleOf = (x: Item) => t(x.id === "menuTrades" ? axesSettingsTitle({ trades: uses(m, "trades"), places: uses(m, "places"), items: uses(m, "items") }) : x.title);
  const sections = (admin ? ADMIN_SECTIONS : MEMBER_SECTIONS)
    .map((s) => ({ ...s, items: s.items.filter((x) => (schedule || !x.schedule) && (!x.module || m.tenant.modules.includes(x.module)) && (!x.anyOf || x.anyOf.some((k) => uses(m, k))) && !pcOnly.hidden(x.to)) }))
    .filter((s) => s.items.length > 0);

  return (
    <div className="setmenu" data-testid="settings-menu-list">
      <GroupSection note={admin ? undefined : <span id="memberNote">{SETTINGS_MSG.memberNote}</span>} data-testid="settings-head">
        <GroupList>
          <GroupHeadRow
            icon={<CompanyIcon name={name} logoUrl={company.data?.logo?.url} size={56} />}
            title={name || SWITCHER_MSG.noName}
            titleTestId="settings-head-name"
            sub={admin ? SETTINGS_MSG.companyRowSub : ROLE_LABEL.member}
            to={admin ? "/settings/company" : undefined}
            current={cur === "/settings/company"}
            buttonId="menuCompany"
            className={name ? undefined : "is-noname"}
          />
        </GroupList>
      </GroupSection>
      {sections.map((s) => (
        <GroupSection key={s.key} title={t(s.title)} data-testid={`settings-sec-${s.key}`}>
          <GroupList>
            {s.items.map((x) => (
              <GroupNavRow
                key={x.to}
                icon={<RowIcon k={x.icon} />}
                title={titleOf(x)}
                value={valueOf(x.value)}
                valueTestId={x.value ? `menu-${x.value}-value` : undefined}
                to={x.to}
                buttonId={x.id}
                current={cur === x.to}
              />
            ))}
          </GroupList>
        </GroupSection>
      ))}
      {closeShown && deletable ? (
        <GroupSection note={COMPANY_DELETE_MSG.note} data-testid="settings-sec-delete">
          <GroupList>
            <GroupDangerRow title={COMPANY_DELETE_MSG.row} onClick={() => setAskDelete(true)} buttonId="menuDeleteCompany" />
          </GroupList>
        </GroupSection>
      ) : closeShown ? (
        <GroupSection note={SETTINGS_MSG.closeNote} data-testid="settings-sec-close">
          <GroupList>
            <GroupDangerRow title="退会" to="/settings/close" buttonId="menuClose" current={cur === "/settings/close"} />
          </GroupList>
        </GroupSection>
      ) : null}
      {askDelete ? <CompanyDeleteDialog name={name || SWITCHER_MSG.noName} onClose={() => setAskDelete(false)} /> : null}
    </div>
  );
}

/** 設定（/settings）。スマホ版はメニューの画面、PC版は2列の右の最初の画面へ置き換える（★3） */
export function SettingsMenuPage() {
  usePageName(SETTINGS_MSG.title);
  const me = useMe();
  // 上の帯「‹ 前の画面」（docs/62 §2）。直前の画面が無ければ 今日
  const back = useBackNav({ to: "/home", label: HOME_MSG.menu });
  if (currentView() === "pc") {
    if (!me.data) return null;
    return <Navigate to={me.data.user.role === "admin" ? "/settings/company" : "/settings/templates"} replace />;
  }
  return (
    <div className="ppage setpage" data-testid="settings-menu">
      <div className="cotop">
        <BackButton back={back} />
        <span className="cotop-sp" />
      </div>
      <h1 className="ptitle">{SETTINGS_MSG.title}</h1>
      <SettingsMenu />
    </div>
  );
}
